import React from "react";

/* Insights, News, FAQ and Members had their own pages on the old site. They do
   not exist in this build, so the teasers below stay as content but no longer
   link anywhere — a "More detail" button that 404s is worse than none. */
const PR_PREVIEWS = [
  {
    id: "insights", eyebrow: "Field notes", title: "Insights",
    blurb: "Long-form pieces and working notes from the studio.",
    accent: "var(--indigo)", accentSoft: "var(--indigo-soft)", accentDeep: "var(--indigo-deep)",
    items: [
      { tag: "AI", a: "Why most Myanmar SMEs don't need a chatbot — they need a knowledge spine.", b: "Hnin Wai · 12 min" },
      { tag: "Product", a: "The 6-week MVP, but slower.", b: "Kaung Myat · 9 min" },
      { tag: "Engineering", a: "Bilingual systems: when Myanmar Unicode meets Japanese ops.", b: "Sai Lin · 14 min" },
    ],
  },
  {
    id: "news", eyebrow: "Newsroom", title: "News",
    blurb: "Announcements, releases, press, and events.",
    accent: "var(--vermilion)", accentSoft: "oklch(94% 0.04 35)", accentDeep: "var(--vermilion-deep)",
    items: [
      { tag: "Announcement", a: "NEXT INNOVATIONS opens a Tokyo satellite studio.", b: "May 12, 2026 · Tokyo" },
      { tag: "Product", a: "SmartPost AI 2.0 ships with Burmese-Japanese cross-posting.", b: "Apr 30, 2026 · Yangon" },
      { tag: "Partnership", a: "TikTok for Business — official agency partnership for MM.", b: "Apr 18, 2026 · Yangon" },
    ],
  },
  {
    id: "faq", eyebrow: "Answered", title: "FAQ",
    blurb: "The honest answers we give before the first call.",
    accent: "oklch(58% 0.10 85)", accentSoft: "oklch(94% 0.04 85)", accentDeep: "oklch(38% 0.10 85)",
    items: [
      { tag: "AI", a: "We want \u201Can AI chatbot.\u201D Where do we start?", b: "AI Solutions · 4 questions" },
      { tag: "PRODUCT", a: "How long does an MVP usually take?", b: "Product / MVP · 4 questions" },
      { tag: "PRICING", a: "Fixed-price or time-and-materials?", b: "Engagement · 4 questions" },
    ],
  },
];

/* Mirrors members.jsx (the /members page roster) — same people, roles,
   locations, hues, and bios so the home strip and the full team page agree. */
const MEMBERS_STRIP = [
  /* Leadership */
  { kind: "human", name: "Yuta Mukai", role: "CEO", team: "Leadership", loc: "YGN", hue: 270, photo: null,
    bio: "Sets direction and owns the studio's client relationships." },
  { kind: "human", name: "Sai Ye Khaung", role: "Business Consultant", team: "Leadership", loc: "YGN", hue: 270, photo: null,
    bio: "Bridges client goals and delivery. Owns scoping and commercial strategy." },
  { kind: "human", name: "Min Min Latt", role: "Engineering Leader", team: "Leadership", loc: "YGN", hue: 270, photo: null,
    bio: "Sets the engineering bar and reviews architecture across every build." },
  /* Engineering */
  { kind: "human", name: "Khant Hmue", role: "Full-stack Engineer · AI / Web", team: "Engineering", loc: "YGN", hue: 158, photo: null,
    bio: "Builds AI features and web platforms end to end." },
  { kind: "human", name: "Shine Htet Aung", role: "Full-stack Engineer · Mobile / Web", team: "Engineering", loc: "YGN", hue: 158, photo: null,
    bio: "Ships mobile and web apps from prototype to production." },
  { kind: "human", name: "Than Myo", role: "Full-stack Engineer · AI / Web", team: "Engineering", loc: "YGN", hue: 158, photo: null,
    bio: "Works across retrieval, tooling, and web delivery." },
  { kind: "human", name: "Thura Nyi", role: "Full-stack Engineer · Backend / Infra", team: "Engineering", loc: "YGN", hue: 158, photo: null,
    bio: "Owns backend services, infrastructure, and reliability." },
  /* Engineering · AI agents */
  { kind: "agent", codename: "AI · Web Agent", role: "Autonomous coding agent", team: "Engineering", model: "agent", version: "v1",
    bio: "Drafts features, writes tests, and opens PRs under engineer review." },
  { kind: "agent", codename: "Mobile · Web Agent", role: "Autonomous coding agent", team: "Engineering", model: "agent", version: "v1",
    bio: "Generates UI scaffolds and screen flows for review." },
  { kind: "agent", codename: "Backend · Infra Agent", role: "Autonomous ops agent", team: "Engineering", model: "agent", version: "v1",
    bio: "Watches services, drafts runbooks, and proposes fixes." },
  /* Operations */
  { kind: "human", name: "Aye Aye Kyaw", role: "People & Office Operations", team: "Operations", loc: "YGN", hue: 320, photo: null,
    bio: "Runs hiring, office, and the day-to-day that keeps the studio moving." },
  /* UI / UX Design */
  { kind: "human", name: "Myat Min", role: "Product Designer · UI / UX", team: "UI / UX Design", loc: "YGN", hue: 85, photo: null,
    bio: "Owns interface design, prototyping, and the studio's design system." },
];

const MEMBERS_HUMAN_COUNT = MEMBERS_STRIP.filter((m) => m.kind !== "agent").length;
const MEMBERS_AGENT_COUNT = MEMBERS_STRIP.filter((m) => m.kind === "agent").length;

const TRUST_TEAM = [
  {
    name: "Yuta Mukai",
    role: "CEO / Product Strategy",
    tag: "Business & product direction",
    copy: "Turns business goals into product direction, scope, and market fit.",
    hue: 270,
    signal: "12+ yrs Myanmar",
    photo: null,
  },
  {
    name: "Min Min Latt",
    role: "Engineering Leader",
    tag: "Architecture & delivery quality",
    copy: "Leads engineering decisions, system design, and delivery standards.",
    hue: 158,
    signal: "AI + system flow",
    photo: null,
  },
  {
    name: "Sai Ye Khaung",
    role: "Business Consultant",
    tag: "Local coordination",
    copy: "Connects business requirements, local operations, and execution.",
    hue: 35,
    signal: "Myanmar operations",
    photo: null,
  },
  {
    name: "AI Agents",
    role: "Supervised AI workflow",
    tag: "Speed with review",
    copy: "AI agents support coding, research, QA, and documentation under human review.",
    hue: 230,
    signal: "Human supervised",
    agent: true,
    photo: null,
  },
];

function PreviewSurface({ p, i }) {
  const loop = [...p.items, ...p.items];
  const durationS = 14;
  return (
    <article
      className="preview-card"
      style={{
        background: "var(--paper)", border: "1px solid var(--line)",
        borderRadius: "var(--r-4)", padding: 0,
        display: "flex", flexDirection: "column", overflow: "hidden",
        position: "relative",
        transition: "transform var(--dur-base) var(--ease-rain), box-shadow var(--dur-base), border-color var(--dur-base)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "var(--shadow-2)";
        e.currentTarget.style.borderColor = "var(--line-strong)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = "var(--line)";
      }}
    >
      <div style={{
        height: 4,
        background: `linear-gradient(90deg, ${p.accent} 0%, ${p.accent} 35%, transparent 100%)`,
      }} />

      <div style={{ padding: "24px 24px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
        <div className="flex items-center between">
          <span className="mono" style={{ fontSize: 10, color: "var(--fg-subtle)", letterSpacing: "0.12em" }}>
            0{i + 1} · {p.eyebrow.toUpperCase()}
          </span>
          <span style={{
            width: 28, height: 28, display: "flex",
            alignItems: "center", justifyContent: "center", borderRadius: 999,
            background: p.accentSoft, color: p.accentDeep,
            fontFamily: "var(--font-sans)", fontStyle: "normal",
            fontSize: 15, letterSpacing: "-0.02em",
            border: `1px solid ${p.accentSoft}`,
          }}>{i + 1}</span>
        </div>
        <h3 style={{ fontSize: 28, letterSpacing: "-0.02em", lineHeight: 1.05 }}>{p.title}</h3>
        <p style={{ fontSize: 13.5, color: "var(--fg-muted)", lineHeight: 1.55 }}>{p.blurb}</p>
      </div>

      <div className="preview-viewport" style={{
        position: "relative", margin: "0 16px",
        height: 270, overflow: "hidden",
        borderTop: "1px solid var(--line-soft)", borderBottom: "1px solid var(--line-soft)",
        maskImage: "linear-gradient(to bottom, transparent 0, #000 14%, #000 86%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to bottom, transparent 0, #000 14%, #000 86%, transparent 100%)",
      }}>
        <div className="preview-track" style={{
          display: "flex", flexDirection: "column", gap: 10, padding: "12px 0",
          animation: `preview-scroll ${durationS}s linear infinite`,
        }}>
          {loop.map((it, idx) => (
            <div key={idx}
              style={{
                color: "inherit", padding: "14px 14px",
                background: "var(--paper-2)", border: "1px solid var(--line-soft)",
                borderRadius: "var(--r-3)",
                display: "flex", flexDirection: "column", gap: 6,
                flexShrink: 0,
                transition: "background var(--dur-snap), border-color var(--dur-snap)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#FFFCF6";
                e.currentTarget.style.borderColor = "var(--line)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "var(--paper-2)";
                e.currentTarget.style.borderColor = "var(--line-soft)";
              }}
            >
              <span className="mono" style={{
                fontSize: 9.5, color: p.accentDeep,
                letterSpacing: "0.12em", fontWeight: 500,
              }}>{it.tag.toUpperCase()}</span>
              <span style={{
                fontSize: 14, lineHeight: 1.4, fontWeight: 500, letterSpacing: "-0.005em",
                display: "-webkit-box", WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical", overflow: "hidden",
              }}>{it.a}</span>
              <span className="mono" style={{
                fontSize: 10, color: "var(--fg-subtle)", letterSpacing: "0.06em",
              }}>{it.b}</span>
            </div>
          ))}
        </div>
      </div>
    </article>
  );
}

function TrustMemberCard({ m, ...props }) {
  const initials = m.agent ? "AI" : m.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div
      className={"trust-member-card" + (m.agent ? " is-agent" : "")}
      style={{ "--member-hue": m.hue }}
      {...props}
    >
      <div className="trust-member-photo">
        {m.photo ? (
          <img src={m.photo} alt={`${m.name} profile`} loading="lazy" />
        ) : (
          <span>{initials}</span>
        )}
      </div>
      <div className="trust-member-copy">
        <div className="trust-member-topline">
          <span className="mono">{m.tag}</span>
          <span className="trust-member-signal">{m.signal}</span>
        </div>
        <h4>{m.name}</h4>
        <div className="trust-member-role">{m.role}</div>
        <p>{m.copy}</p>
      </div>
    </div>
  );
}

function MembersStrip({ i }) {
  const humans = MEMBERS_STRIP.filter((m) => m.kind === "human");
  const ygn = humans.filter((m) => m.loc === "YGN").length;

  return (
    <article
      className="preview-card members-strip members-strip--scroll"
      style={{
        marginTop: 28,
        background: "var(--paper)", border: "1px solid var(--line)",
        borderRadius: "var(--r-4)", padding: 0,
        display: "flex", flexDirection: "column", overflow: "hidden",
        position: "relative",
        transition: "transform var(--dur-base) var(--ease-rain), box-shadow var(--dur-base), border-color var(--dur-base)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-3px)";
        e.currentTarget.style.boxShadow = "var(--shadow-2)";
        e.currentTarget.style.borderColor = "var(--line-strong)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.borderColor = "var(--line)";
      }}
    >
      <div style={{
        height: 4,
        background: "linear-gradient(90deg, var(--moss) 0%, var(--moss) 22%, transparent 100%)",
      }} />

      <div style={{
        padding: "24px 28px 18px",
        display: "grid", gridTemplateColumns: "1fr auto",
        gap: 20, alignItems: "end",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <span className="mono" style={{ fontSize: 10, color: "var(--fg-subtle)", letterSpacing: "0.12em" }}>
            0{i + 1} · THE STUDIO
          </span>
          <div style={{ display: "flex", alignItems: "baseline", gap: 18, flexWrap: "wrap" }}>
            <h3 style={{ fontSize: 28, letterSpacing: "-0.02em", lineHeight: 1.05 }}>Members</h3>
            <p style={{ fontSize: 13.5, color: "var(--fg-muted)", lineHeight: 1.55, maxWidth: 540 }}>
              The people and supervised AI agents behind your project — strategy, architecture,
              execution, and local coordination in one team.
            </p>
          </div>
        </div>
        <div className="mono members-strip-meta" style={{
          fontSize: 11, color: "var(--fg-subtle)",
          letterSpacing: "0.1em", textAlign: "right",
        }}>
          {MEMBERS_HUMAN_COUNT} PEOPLE · YGN {ygn} · {MEMBERS_AGENT_COUNT} AGENTS
        </div>
      </div>

      <div className="trust-members-grid" aria-label="Featured studio members">
        <div className="members-track">
          {[...TRUST_TEAM, ...TRUST_TEAM].map((m, idx) => (
            <TrustMemberCard
              key={`${m.name}-${idx}`}
              m={m}
              aria-hidden={idx >= TRUST_TEAM.length ? "true" : undefined}
            />
          ))}
        </div>
      </div>
    </article>
  );
}

function Previews() {
  return (
    <section className="section-pad" id="previews" style={{ background: "var(--paper-inset)", position: "relative" }}>
      <div aria-hidden="true" style={{
        position: "absolute", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(to right, transparent calc(50% - 0.5px), var(--line-soft) calc(50% - 0.5px), var(--line-soft) calc(50% + 0.5px), transparent calc(50% + 0.5px))",
        opacity: 0.5,
      }} />

      <div className="container" style={{ position: "relative" }}>
        <div className="flex items-center between" style={{ marginBottom: 56, flexWrap: "wrap", gap: 24 }}>
          <div style={{ maxWidth: 720 }}>
            <div className="eyebrow">Explore Next</div>
            <h2 style={{ marginTop: 16 }}>
              Learn more about{" "}
              <span style={{ color: "var(--indigo)" }}>how we think and build.</span>
            </h2>
          </div>
          <div className="mono" style={{
            fontSize: 11, color: "var(--fg-subtle)",
            letterSpacing: "0.1em", whiteSpace: "nowrap",
          }}>04 · CATEGORIES · LIVE</div>
        </div>

        <div className="previews-grid">
          {PR_PREVIEWS.map((p, i) => (<PreviewSurface key={p.id} p={p} i={i} />))}
        </div>

        <MembersStrip i={PR_PREVIEWS.length} />
      </div>
    </section>
  );
}

export { Previews };
